import L from 'leaflet';
import { type ReactElement, useEffect, useRef } from 'react';
import HashQuery from '../HashQuery/HashQuery';
import { Icon } from '../Icon/Icon';
import { logEvent } from '../Util';
import { useQueryParam } from '../Util/useQueryParam';
import { FullscreenControl, LocateControl } from './Controls';
import { type MapProps } from './Map.types';
import { MapSetup } from './MapSetup';
import './Mixins/SpinnerMixin';

export function LeafletMap({
    containerId = 'map',
    mapServerUrl,
    mapTilesVersion,
    startCenter,
    startZoom,
    savePositionInHash,
    disableFullscreenControl,
    fullscreenControlPosition,
    showMyPosition,
    scrollWheelZoom,
    lockPanZoom,
    standardZoomLayout,
    startOnMyPosition,
    startOnFeatureBounds,
    regionExtent,
    showMobileFullscreenOverlay,
    onMapInitiated,
    onMapUnloaded,
}: Readonly<MapProps>): ReactElement {
    const mapRef = useRef<L.Map>();
    const mapContainerRef = useRef<HTMLDivElement>();

    const [hasFullscreenQuery, setFullscreenQuery, removeFullscreenQuery] = useQueryParam('fs');

    const enableFullscreen = () => {
        setFullscreenQuery('1');
    };

    const disableFullscreen = () => {
        removeFullscreenQuery();
    };

    useEffect(() => {
        let hashPosition: number[] = null;
        let hashZoom: number = null;

        L.Icon.Default.imagePath = '/images/map/';

        if (savePositionInHash) {
            hashPosition = HashQuery.getPosition();
            hashZoom = HashQuery.getZoom();
        }

        startCenter = hashPosition ? L.GeoJSON.coordsToLatLng(hashPosition.reverse() as [number, number]) : L.latLng(startCenter);
        startZoom = hashZoom || startZoom;

        const map = (mapRef.current = new MapSetup(mapContainerRef.current, {
            mapServerUrl: mapServerUrl,
            mapTilesVersion: mapTilesVersion,
            center: startCenter,
            zoom: startZoom,
        }));

        if (!scrollWheelZoom) {
            map.scrollWheelZoom.disable();
        }

        if (!lockPanZoom) {
            if (standardZoomLayout) {
                new L.Control.Zoom().addTo(map);
            } else {
                const zoom = new L.Control.Zoom({
                    position: 'bottomright',
                    zoomInTitle: 'Zooma in',
                    zoomOutTitle: 'Zooma ut',
                    zoomOutText: '<span class="icon-minus"></span>',
                    zoomInText: '<span class="icon-plus"></span>',
                }).addTo(map);

                const zoomContainer = zoom.getContainer();
                if (zoomContainer) {
                    L.DomUtil.addClass(zoomContainer, 'button button--map');
                }
            }
        }

        let locateControl: LocateControl;
        if (showMyPosition) {
            locateControl = new LocateControl().addTo(map).on('locationmarkerclicked', () => {
                logEvent('Map', 'My position requested');
            });
            if (startOnMyPosition) {
                locateControl.initializeView(L.geoJSON(regionExtent).getBounds());
            }
        }

        if (startOnFeatureBounds) {
            map.once(
                'layeraddcomplete',
                () => {
                    let bounds;
                    map.eachLayer((layer) => {
                        if (layer instanceof L.MarkerClusterGroup) {
                            bounds = bounds ? bounds.extend(layer.getBounds()) : layer.getBounds();
                        }
                    });
                    if (bounds?.isValid()) {
                        map.fitBounds(bounds, { maxZoom: 9 });
                    }
                },
                this,
            );
        }

        const scaleControl = L.control.scale({ imperial: false, updateWhenIdle: true });

        scaleControl.addTo(map);

        if (!disableFullscreenControl) {
            const fullscreenControl = new FullscreenControl({
                position: (fullscreenControlPosition || 'topright') as L.ControlPosition,
            });

            fullscreenControl.on('enable', enableFullscreen, this).on('disable', disableFullscreen, this).addTo(map);

            // Fix url for users using the old fullscreen flag that lived in the hash
            if (HashQuery.hasQueryKey('fullscreen')) {
                HashQuery.removeQueryKey('fullscreen', false);

                enableFullscreen();
            }

            // Restore fullscreen state on popstate (back/forward navigation)
            window.addEventListener('popstate', () => {
                const shouldEnableFullscreen = window.location.search.includes('fs=1');
                if (shouldEnableFullscreen) {
                    enableFullscreen();
                } else {
                    disableFullscreen();
                }
            });
        }

        if (savePositionInHash) {
            map.on(
                'moveend',
                (e) => {
                    HashQuery.setMapPosition(L.GeoJSON.latLngToCoords(e.target.getCenter()).reverse() as [number, number], e.target.getZoom());
                },
                this,
            );
        }

        onMapInitiated?.(map);

        return () => {
            onMapUnloaded?.(map);
        };
    }, []);

    useEffect(() => {
        if (hasFullscreenQuery === '1') {
            _enableFullscreen();
        } else {
            _disableFullscreen();
        }
    }, [hasFullscreenQuery]);

    const _enableFullscreen = (): void => {
        const map = mapRef.current;
        const mapContainer = mapContainerRef.current;
        if (map && !mapContainer.classList.contains('fullscreen')) {
            mapContainer.classList.add('fullscreen');
            document.documentElement.classList.add('fullscreen');

            map.scrollWheelZoom.enable();
            map.invalidateSize();
            const viewport = document.querySelector('meta[name=viewport]');
            viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0');

            map.fire('enablefullscreen');
        }

        logEvent('Map', 'Fullscreen enabled');
    };

    const _disableFullscreen = (): void => {
        const map = mapRef.current;
        const mapContainer = mapContainerRef.current;
        if (map && mapContainer.classList.contains('fullscreen')) {
            mapContainer.classList.remove('fullscreen');
            document.documentElement.classList.remove('fullscreen');

            map.scrollWheelZoom.disable();
            map.invalidateSize();
            const viewport = document.querySelector('meta[name=viewport]');
            viewport.setAttribute('content', 'width=device-width, initial-scale=1.0');

            map.fire('disablefullscreen');
        }

        logEvent('Map', 'Fullscreen disabled');
    };

    const renderMobileFullscreenLinkOverlay = (): JSX.Element => {
        return (
            <button onClick={() => enableFullscreen()} className="btn btn-link unstyled-button mobile-link-navigation">
                <span className="mobile-link-overlay">
                    <span className="d-flex align-items-center">
                        <span className="mobile-link-map_icon">
                            <Icon symbol="icon-map" />
                        </span>
                        <span className="mobile-link-map_text">Karta</span>
                    </span>
                </span>
            </button>
        );
    };

    return (
        <>
            <div id={containerId} className="map-element" ref={mapContainerRef} />
            {showMobileFullscreenOverlay && renderMobileFullscreenLinkOverlay()}
        </>
    );
}
